import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Checkbox } from "antd";
import { GET_USER_ORDERS } from "../../api/graphql/orders";
import { AndroidFilled, AppleFilled } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import styles from "./styles.module.scss";
import { useUser } from '../../context/UserContext';
import CustomSelect from '../CustomSelect';
import Page from "../Page";
import DataTable, { DataColumn } from "../DataTable";
import StatusBadge from "../StatusBadge";
import { FilterPanel, SearchInput, Toolbar, filterStyles } from "../Filters";
import DateRangeFilter from "../DateRangeFilter";
import {
  formatInstallRate,
  formatOrderType,
  getTotalInstalls,
  matchesOrderType,
  ORDER_TYPE_OPTIONS,
} from "../../utils/formatters";
import { isInternalUser } from "../../utils/internalUsers";
import ManagerSelect, { managerName } from "../ManagerSelect";
import useOrderManagers, {
  ALL_MANAGERS,
  managerFilterOptions,
  matchesManager,
} from "../ManagerSelect/useOrderManagers";

dayjs.extend(isBetween);

export function getStoreIcon(store: any) {
  switch (store) {
    case "GOOGLE_STORE":
      return (
        <AndroidFilled className={styles.storeIcon} />
      );
    case "APP_STORE":
      return <AppleFilled className={styles.storeIcon} />;
  }
}

export type SelectedWeek = "last-week" | "this-week" | "last-month" | "all";
export type DateFilterType = "week" | "month" | "quarter";

type RangeValue = [Dayjs | null, Dayjs | null] | null;

const CampaignTable: React.FC = () => {
  const { data: campaignsList, loading } = useQuery(GET_USER_ORDERS, {
    fetchPolicy: "no-cache"
  });
  const [searchApp, setSearchApp] = useState("");
  const [periodFilter, setPeriodFilter] = useState<DateFilterType | null>(null);
  const [dateRange, setDateRange] = useState<RangeValue>(null);
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedManager, setSelectedManager] = useState(ALL_MANAGERS);
  const [showInternal, setShowInternal] = useState(false);
  const { user } = useUser();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const { orderManager, effectiveManager, assign } = useOrderManagers();
  const managerOptions = useMemo(
    () => managerFilterOptions(campaignsList?.getAllOrders || [], orderManager),
    [campaignsList, orderManager],
  );

  const columns: DataColumn<any>[] = [
    {
      title: "ID",
      key: "hashId",
      width: 96,
      fixed: true,
      // The ID opens the campaign page.
      render: (record) => (
        <Link
          to={`/campaigns/${record.orderType}/${record.id}`}
          className={styles.link}
        >
          {record.hashId}
        </Link>
      ),
      sorter: (a, b) => a.hashId - b.hashId,
    },
    {
      title: "Manager",
      key: "manager",
      width: 176,
      mobile: "full",
      // Only a super admin assigns managers; the others see the name.
      render: (record) =>
        isSuperAdmin ? (
          <ManagerSelect
            value={orderManager(record)}
            inherited={record.user?.manager}
            onChange={(manager) => assign(record, manager)}
          />
        ) : (
          managerName(effectiveManager(record)) || "—"
        ),
      sorter: (a, b) =>
        managerName(effectiveManager(a)).localeCompare(
          managerName(effectiveManager(b)),
        ),
    },
    {
      title: "Email",
      key: "user",
      width: 240,
      mobile: "full",
      render: (record) =>
        record.user?.email ? (
          <span
            className={styles.link}
            title={record.user.email}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/user/${record.user.id}`);
            }}
          >
            {record.user.email}
          </span>
        ) : (
          "—"
        ),
      sorter: (a, b) =>
        (a.user?.email || "").localeCompare(b.user?.email || ""),
    },
    {
      title: "Platform",
      key: "platform",
      width: 112,
      align: "center",
      render: (record) =>
        record.app?.store ? getStoreIcon(record.app.store) : null,
      sorter: (a, b) => (a.app?.store || "").localeCompare(b.app?.store || ""),
    },
    {
      title: "App",
      key: "app",
      width: 240,
      mobile: "full",
      render: (record) => record.app?.title,
      sorter: (a, b) => (a.app?.title || "").localeCompare(b.app?.title || ""),
    },
    {
      title: "GEO",
      key: "geo",
      width: 88,
      align: "center",
      render: (record) =>
        record.app?.country ? record.app.country.toUpperCase() : "",
      sorter: (a, b) =>
        (a.app?.country || "").localeCompare(b.app?.country || ""),
    },
    {
      title: "Type",
      key: "type",
      width: 128,
      render: (record) =>
        formatOrderType(record.orderType, record.subtype) || "—",
      sorter: (a, b) =>
        formatOrderType(a.orderType, a.subtype).localeCompare(
          formatOrderType(b.orderType, b.subtype),
        ),
    },
    {
      title: "Status",
      key: "status",
      width: 128,
      mobile: "header",
      render: (record) => <StatusBadge status={record.status} />,
      sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
    },
    {
      title: "Sell",
      key: "sell",
      width: 120,
      render: (record) =>
        record.orderPrice ? `$${record.orderPrice.toFixed(2)}` : "$0.00",
      sorter: (a, b) => (a.orderPrice || 0) - (b.orderPrice || 0),
    },
    {
      title: "Installs",
      key: "installs",
      width: 112,
      // Ratings and reviews have no installs.
      render: (record) => record.totalInstalls ?? "—",
      sorter: (a, b) => (a.totalInstalls || 0) - (b.totalInstalls || 0),
    },
    {
      title: "Price / install",
      key: "pricePerInstall",
      width: 152,
      render: (record) => formatInstallRate(record.pricePerInstall),
      sorter: (a, b) => (a.pricePerInstall || 0) - (b.pricePerInstall || 0),
    },
    {
      title: "Start Date",
      key: "startDate",
      width: 128,
      render: (record) =>
        record.initialDate
          ? dayjs(record.initialDate).format("DD.MM.YYYY")
          : "N/A",
      sorter: (a, b) =>
        dayjs(a.initialDate).valueOf() - dayjs(b.initialDate).valueOf(),
    },
    {
      title: "End Date",
      key: "endDate",
      width: 120,
      render: (record) =>
        record.endDate ? dayjs(record.endDate).format("DD.MM.YYYY") : "N/A",
      sorter: (a, b) => dayjs(a.endDate).valueOf() - dayjs(b.endDate).valueOf(),
    },
  ];

  const filteredData = useMemo(() => {
    const normalizedStatusFilter = (selectedStatus ?? "all").toLowerCase();
    const normalizedCountryFilter = (selectedCountry ?? "all").toLowerCase();
    const normalizedPlatformFilter = (selectedPlatform ?? "all").toLowerCase();
    const normalizedSearchValue = (searchApp || "").trim().toLowerCase();
    const data = campaignsList?.getAllOrders || [];
    console.log(data)
    const filtered = data.filter((item: any) => {
      // Internal (test) accounts stay out unless asked for
      if (!showInternal && isInternalUser(item.user)) return false;

      // Status filter
      const matchesStatus =
        normalizedStatusFilter === "all" ||
        (item.status?.toLowerCase() || "") === normalizedStatusFilter;

      // Country filter
      const matchesCountry =
        normalizedCountryFilter === "all" ||
        (item.app?.country?.toLowerCase() || "") === normalizedCountryFilter;

      // Platform filter
      const matchesPlatform =
        normalizedPlatformFilter === "all" ||
        (item.app?.store?.toLowerCase() || "") === normalizedPlatformFilter;

      const matchesType = matchesOrderType(
        selectedType,
        item.orderType,
        item.subtype,
      );

      // The manager the Manager column shows: the order's own, else the client's.
      const matchesManagerFilter = matchesManager(
        selectedManager,
        effectiveManager(item),
      );

      // Order ID / Email filter
      const matchesOrderId =
        !normalizedSearchValue ||
        (() => {
          const hashId = String(item.hashId || "");
          const orderId = String(item.id || "");
          const email = (item.user?.email || "").toLowerCase();

          return (
            hashId.includes(normalizedSearchValue) ||
            orderId.includes(normalizedSearchValue) ||
            email.includes(normalizedSearchValue)
          );
        })();

      // Date filter
      const matchesDate = (() => {
        if (dateRange && dateRange[0] && dateRange[1]) {
          let itemDate;
          if (item.initialDate) {
            const [day, month, year] = item.initialDate.split("/");
            itemDate = dayjs(`${year}-${month}-${day}`);
          } else if (item.createdAt) {
            itemDate = dayjs(parseInt(item.createdAt));
          } else {
            return false;
          }
          const [start, end] = dateRange;
          return itemDate.isBetween(start, end, 'day', '[]');
        } else if (periodFilter) {
          let itemDate;
          if (item.initialDate) {
            const [day, month, year] = item.initialDate.split("/");
            itemDate = dayjs(`${year}-${month}-${day}`);
          } else if (item.createdAt) {
            itemDate = dayjs(parseInt(item.createdAt));
          } else {
            return false;
          }
          const now = dayjs();
          switch (periodFilter) {
            case 'week':
              return itemDate.isBetween(now.startOf('week'), now.endOf('week'), 'day', '[]');
            case 'month':
              return itemDate.isBetween(now.startOf('month'), now.endOf('month'), 'day', '[]');
            case 'quarter': {
              const quarterStartMonth = Math.floor(now.month() / 3) * 3;
              const start = now.month(quarterStartMonth).startOf('month');
              const end = start.add(2, 'month').endOf('month');
              return itemDate.isBetween(start, end, 'day', '[]');
            }
            default:
              return true;
          }
        }
        return true;
      })();

      return (
        matchesStatus &&
        matchesCountry &&
        matchesPlatform &&
        matchesType &&
        matchesManagerFilter &&
        matchesOrderId &&
        matchesDate
      );
    });

    // Attach a unique, stable-ish key per row to avoid React key collisions
    return filtered.map((item: any, index: number) => ({
      ...item,
      __key: `${item.orderType || "unknown"}-${item.id ?? item.hashId ?? "noid"}-${index}`,
    }));
  }, [
    campaignsList,
    periodFilter,
    dateRange,
    selectedCountry,
    selectedPlatform,
    selectedStatus,
    selectedType,
    selectedManager,
    effectiveManager,
    searchApp,
    showInternal,
  ]);

  // Sum of the rows currently shown, like the Totals row in Campaigns 2.0.
  const totalSell = useMemo(
    () =>
      filteredData.reduce(
        (sum: number, item: any) => sum + (Number(item.orderPrice) || 0),
        0,
      ),
    [filteredData],
  );
  const totalInstalls = useMemo(
    () => getTotalInstalls(filteredData),
    [filteredData],
  );

  // The table starts again from the top when a filter changes.
  const filtersKey = JSON.stringify([
    searchApp,
    periodFilter,
    dateRange,
    selectedCountry,
    selectedPlatform,
    selectedStatus,
    selectedType,
    selectedManager,
    showInternal,
  ]);

  const handlePeriodChange = (e: any) => {
    setPeriodFilter(e.target.value as DateFilterType);
    setDateRange(null); 
  };


  const handleRangeChange = (dates: RangeValue) => {
    setDateRange(dates);
    setPeriodFilter(null);
  };

  // Filters in use: a phone counts them on its Filters button.
  const activeFilters = [
    selectedCountry !== "all",
    selectedPlatform !== "all",
    selectedStatus !== "all",
    selectedType !== "all",
    selectedManager !== ALL_MANAGERS,
    showInternal,
  ].filter(Boolean).length;

  if (loading) return <Page title="Campaigns">Loading...</Page>;

  return (
    <Page title="Campaigns" fitScreen>
      <Toolbar>
        <SearchInput
          placeholder="Search by Id or Email"
          value={searchApp}
          onChange={setSearchApp}
        />
        <DateRangeFilter
          period={periodFilter}
          onPeriodChange={(value) => handlePeriodChange({ target: { value } })}
          range={
            dateRange && dateRange[0] && dateRange[1]
              ? [dateRange[0], dateRange[1]]
              : null
          }
          onRangeChange={handleRangeChange}
        />
      </Toolbar>
      <FilterPanel active={activeFilters}>
        <div className={styles.select}>
          <CustomSelect
            value={selectedCountry}
            onChange={(value) => setSelectedCountry(value)}
            options={[
              { value: "all", label: "All countries" },
              { value: "us", label: "US" },
              { value: "uk", label: "UK" },
              { value: "ca", label: "CA" },
              { value: "de", label: "DE" },
              { value: "fr", label: "FR" },
              { value: "es", label: "ES" },
              { value: "it", label: "IT" },
              { value: "jp", label: "JP" },
              { value: "cn", label: "CN" },
              { value: "br", label: "BR" },
              { value: "ru", label: "RU" },
              { value: "in", label: "IN" },
            ]}
          />
        </div>
        <div className={styles.select}>
          <CustomSelect
            value={selectedPlatform}
            onChange={(value) => setSelectedPlatform(value)}
            options={[
              { value: "all", label: "All platforms" },
              { value: "APP_STORE", label: "iOS" },
              { value: "GOOGLE_STORE", label: "Android" },
            ]}
          />
        </div>
        <div className={styles.select}>
          <CustomSelect
            value={selectedStatus}
            onChange={(value) => setSelectedStatus(value)}
            options={[
              { value: "all", label: "All statuses" },
              { value: "moderation", label: "Moderation" },
              { value: "launching", label: "Launching" },
              { value: "active", label: "Active" },
              { value: "done", label: "Done" },
              { value: "canceled", label: "Canceled" },
            ]}
          />
        </div>
        <div className={styles.select}>
          <CustomSelect
            value={selectedType}
            onChange={setSelectedType}
            options={ORDER_TYPE_OPTIONS}
            aria-label="Order type"
          />
        </div>
        <div className={styles.select}>
          <CustomSelect
            value={selectedManager}
            onChange={setSelectedManager}
            options={managerOptions}
            aria-label="Filter by manager"
          />
        </div>
        <Checkbox
          checked={showInternal}
          onChange={(e) => setShowInternal(e.target.checked)}
          className={filterStyles.checkbox}
        >
          Show internal users
        </Checkbox>
      </FilterPanel>
      <DataTable
        id="campaigns"
        columns={columns}
        rows={filteredData}
        rowKey={(record: any) => record.__key}
        defaultSort={{ key: "startDate", direction: "desc" }}
        resetKey={filtersKey}
        cardOrder={["app", "user", "manager"]}
        totals={{
          hashId: "Totals",
          sell: `$${totalSell.toFixed(2)}`,
          installs: totalInstalls,
        }}
      />
    </Page>
  );
};

export default CampaignTable;
